import {
    RESEARCH_ONTOLOGY_REVIEW_DATE,
    RESEARCH_ONTOLOGY_VERSION,
    ResearchConceptDefinition,
    ResearchConceptDefinitionSchema,
    ResearchDomainId,
} from './researchOntology';

interface ConceptSeed {
    aliases?: string[];
    description: string;
    id: string;
    patterns: string[];
    preferredLabel: string;
    primaryDomainId: ResearchDomainId;
    priority?: number;
    secondaryDomainIds?: ResearchDomainId[];
    valueKinds?: ResearchConceptDefinition['valueKinds'];
}

const defineConcept = (seed: ConceptSeed): ResearchConceptDefinition =>
    ResearchConceptDefinitionSchema.parse({
        aliases: seed.aliases ?? seed.patterns.slice(1),
        description: seed.description,
        id: seed.id,
        mappings: [],
        patterns: seed.patterns,
        preferredLabel: seed.preferredLabel,
        primaryDomainId: seed.primaryDomainId,
        priority: seed.priority ?? 7,
        reviewedAt: RESEARCH_ONTOLOGY_REVIEW_DATE,
        secondaryDomainIds: seed.secondaryDomainIds ?? [],
        status: 'active',
        valueKinds: seed.valueKinds ?? ['narrative'],
        version: RESEARCH_ONTOLOGY_VERSION,
    });

const concepts: ConceptSeed[] = [
    // Financial Reporting & Performance
    {
        id: 'financial.revenue',
        preferredLabel: 'Revenue',
        description: 'Income generated from ordinary activities before expenses.',
        patterns: ['revenue', 'net sales', 'total sales'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 10,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.cost-of-revenue',
        preferredLabel: 'Cost of Revenue',
        description: 'Direct costs attributable to delivered products or services.',
        patterns: ['cost of revenue', 'cost of sales'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.gross-profit',
        preferredLabel: 'Gross Profit',
        description: 'Revenue less directly attributable costs.',
        patterns: ['gross profit'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.ebitda',
        preferredLabel: 'EBITDA',
        description: 'Earnings before interest, taxes, depreciation, and amortization.',
        patterns: ['ebitda', 'adjusted ebitda'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 10,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.operating-income',
        preferredLabel: 'Operating Income',
        description: 'Profit generated by core operations before non-operating items.',
        patterns: ['operating income', 'operating profit'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.net-income',
        preferredLabel: 'Net Income',
        description: 'Profit or loss after all recognized income and expenses.',
        patterns: ['net income', 'net earnings', 'profit after tax'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.earnings-per-share',
        preferredLabel: 'Earnings Per Share',
        description: 'Earnings attributable to each common share.',
        patterns: ['earnings per share', 'diluted eps', 'basic eps'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 8,
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'financial.gross-margin',
        preferredLabel: 'Gross Margin',
        description: 'Gross profit expressed as a percentage of revenue.',
        patterns: ['gross margin'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['percentage'],
    },
    {
        id: 'financial.operating-margin',
        preferredLabel: 'Operating Margin',
        description: 'Operating income expressed as a percentage of revenue.',
        patterns: ['operating margin'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['percentage'],
    },
    {
        id: 'financial.free-cash-flow',
        preferredLabel: 'Free Cash Flow',
        description: 'Cash generated after operating needs and capital expenditures.',
        patterns: ['free cash flow', 'fcf'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 10,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.operating-cash-flow',
        preferredLabel: 'Operating Cash Flow',
        description: 'Cash generated or consumed by operating activities.',
        patterns: ['operating cash flow', 'cash from operations'],
        primaryDomainId: 'financial-reporting-performance',
        priority: 9,
        valueKinds: ['currency'],
    },
    {
        id: 'financial.capital-expenditures',
        preferredLabel: 'Capital Expenditures',
        description: 'Cash invested in long-lived productive assets.',
        patterns: ['capital expenditures', 'capital expenditure', 'capex'],
        primaryDomainId: 'financial-reporting-performance',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        priority: 8,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'financial.working-capital',
        preferredLabel: 'Working Capital',
        description: 'Short-term operating assets net of short-term operating liabilities.',
        patterns: ['working capital'],
        primaryDomainId: 'financial-reporting-performance',
        secondaryDomainIds: ['transactions-contracts-commitments'],
        priority: 8,
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'financial.accounts-receivable',
        preferredLabel: 'Accounts Receivable',
        description: 'Amounts due from customers for delivered goods or services.',
        patterns: ['accounts receivable', 'trade receivables'],
        primaryDomainId: 'financial-reporting-performance',
        valueKinds: ['currency'],
    },
    {
        id: 'financial.inventory',
        preferredLabel: 'Inventory',
        description: 'Goods and materials held for production or sale.',
        patterns: ['inventory', 'inventories'],
        primaryDomainId: 'financial-reporting-performance',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'financial.deferred-revenue',
        preferredLabel: 'Deferred Revenue',
        description: 'Consideration received before the related performance obligation is satisfied.',
        patterns: ['deferred revenue', 'contract liability'],
        primaryDomainId: 'financial-reporting-performance',
        valueKinds: ['currency'],
    },

    // Capital Structure, Liquidity & Valuation
    {
        id: 'capital.cash',
        preferredLabel: 'Cash & Equivalents',
        description: 'Cash and highly liquid short-term investments.',
        patterns: ['cash and cash equivalents', 'cash equivalents'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 8,
        valueKinds: ['currency'],
    },
    {
        id: 'capital.debt',
        preferredLabel: 'Debt',
        description: 'Borrowed capital and related financing obligations.',
        patterns: ['debt', 'borrowings', 'indebtedness'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 9,
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'capital.net-debt',
        preferredLabel: 'Net Debt',
        description: 'Debt net of cash and cash-equivalent balances.',
        patterns: ['net debt'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 8,
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'capital.leverage',
        preferredLabel: 'Leverage',
        description: 'Debt burden relative to earnings, assets, or equity.',
        patterns: ['leverage', 'levered', 'debt to ebitda'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 9,
        valueKinds: ['ratio'],
    },
    {
        id: 'capital.liquidity',
        preferredLabel: 'Liquidity',
        description: 'Capacity to satisfy obligations as they become due.',
        patterns: ['liquidity', 'liquid resources'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        secondaryDomainIds: ['risk-controls-resilience'],
        priority: 9,
        valueKinds: ['currency', 'ratio', 'narrative'],
    },
    {
        id: 'capital.interest-expense',
        preferredLabel: 'Interest Expense',
        description: 'Financing cost recognized on outstanding obligations.',
        patterns: ['interest expense'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'capital.covenants',
        preferredLabel: 'Covenants',
        description: 'Contractual financial and operating restrictions in financing agreements.',
        patterns: ['covenant', 'covenants'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        secondaryDomainIds: ['transactions-contracts-commitments', 'risk-controls-resilience'],
        priority: 9,
    },
    {
        id: 'capital.enterprise-value',
        preferredLabel: 'Enterprise Value',
        description: 'Market value of operating assets available to all capital providers.',
        patterns: ['enterprise value'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 9,
        valueKinds: ['currency'],
    },
    {
        id: 'capital.price-earnings',
        preferredLabel: 'Price / Earnings',
        description: 'Equity value relative to earnings.',
        patterns: ['price to earnings', 'p/e ratio', 'p/e multiple'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        valueKinds: ['ratio'],
    },
    {
        id: 'capital.ev-ebitda',
        preferredLabel: 'EV / EBITDA',
        description: 'Enterprise value relative to EBITDA.',
        patterns: ['ev/ebitda', 'ev to ebitda'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        priority: 8,
        valueKinds: ['ratio'],
    },
    {
        id: 'capital.dividends',
        preferredLabel: 'Dividends',
        description: 'Distributions of capital or earnings to shareholders.',
        patterns: ['dividend', 'dividends'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'capital.share-repurchases',
        preferredLabel: 'Share Repurchases',
        description: 'Company purchases of its outstanding equity securities.',
        patterns: ['share repurchase', 'share repurchases', 'stock buyback', 'stock repurchase'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        valueKinds: ['currency', 'integer'],
    },
    {
        id: 'capital.cost-of-capital',
        preferredLabel: 'Cost of Capital',
        description: 'Required return demanded by providers of debt and equity.',
        patterns: ['cost of capital', 'weighted average cost of capital', 'wacc'],
        primaryDomainId: 'capital-structure-liquidity-valuation',
        valueKinds: ['percentage'],
    },

    // Portfolio & Investment Analytics
    {
        id: 'portfolio.assets-under-management',
        preferredLabel: 'Assets Under Management',
        description: 'Market value of assets managed on behalf of clients or investors.',
        patterns: ['assets under management', 'aum'],
        primaryDomainId: 'portfolio-investment-analytics',
        priority: 9,
        valueKinds: ['currency'],
    },
    {
        id: 'portfolio.net-asset-value',
        preferredLabel: 'Net Asset Value',
        description: 'Assets less liabilities attributable to an investment vehicle.',
        patterns: ['net asset value', 'nav'],
        primaryDomainId: 'portfolio-investment-analytics',
        priority: 9,
        valueKinds: ['currency'],
    },
    {
        id: 'portfolio.return',
        preferredLabel: 'Portfolio Return',
        description: 'Investment performance over a specified period.',
        patterns: ['portfolio return', 'total return', 'investment return'],
        primaryDomainId: 'portfolio-investment-analytics',
        priority: 8,
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'portfolio.benchmark',
        preferredLabel: 'Benchmark',
        description: 'Reference index or standard used to evaluate performance.',
        patterns: ['benchmark', 'reference index'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['narrative', 'series'],
    },
    {
        id: 'portfolio.alpha',
        preferredLabel: 'Alpha',
        description: 'Return not explained by the selected market or factor benchmark.',
        patterns: ['alpha'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['percentage', 'ratio'],
    },
    {
        id: 'portfolio.beta',
        preferredLabel: 'Beta',
        description: 'Sensitivity of returns to a benchmark or risk factor.',
        patterns: ['beta'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['ratio'],
    },
    {
        id: 'portfolio.volatility',
        preferredLabel: 'Volatility',
        description: 'Dispersion of investment returns over time.',
        patterns: ['volatility', 'standard deviation'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['percentage', 'ratio'],
    },
    {
        id: 'portfolio.drawdown',
        preferredLabel: 'Drawdown',
        description: 'Decline from a prior portfolio or security peak.',
        patterns: ['drawdown', 'maximum drawdown'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['percentage'],
    },
    {
        id: 'portfolio.sharpe-ratio',
        preferredLabel: 'Sharpe Ratio',
        description: 'Excess return per unit of return volatility.',
        patterns: ['sharpe ratio'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['ratio'],
    },
    {
        id: 'portfolio.exposure',
        preferredLabel: 'Exposure',
        description: 'Economic sensitivity to an asset, sector, geography, or factor.',
        patterns: ['portfolio exposure', 'net exposure', 'gross exposure'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'portfolio.attribution',
        preferredLabel: 'Performance Attribution',
        description: 'Allocation of investment performance to its underlying drivers.',
        patterns: ['performance attribution', 'return attribution'],
        primaryDomainId: 'portfolio-investment-analytics',
    },
    {
        id: 'portfolio.fund-flows',
        preferredLabel: 'Fund Flows',
        description: 'Investor subscriptions, contributions, withdrawals, and redemptions.',
        patterns: ['fund flows', 'net inflows', 'net outflows'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['currency', 'series'],
    },
    {
        id: 'portfolio.management-fee',
        preferredLabel: 'Management Fee',
        description: 'Recurring fee charged for managing investment assets.',
        patterns: ['management fee', 'management fees'],
        primaryDomainId: 'portfolio-investment-analytics',
        valueKinds: ['currency', 'percentage'],
    },

    // Operating Metrics & Unit Economics
    {
        id: 'operations.arr',
        preferredLabel: 'ARR',
        description: 'Annualized recurring revenue under active customer arrangements.',
        patterns: ['annual recurring revenue', 'arr'],
        primaryDomainId: 'operating-metrics-unit-economics',
        priority: 9,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'operations.mrr',
        preferredLabel: 'MRR',
        description: 'Monthly recurring revenue under active customer arrangements.',
        patterns: ['monthly recurring revenue', 'mrr'],
        primaryDomainId: 'operating-metrics-unit-economics',
        priority: 8,
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'operations.net-revenue-retention',
        preferredLabel: 'Net Revenue Retention',
        description: 'Recurring revenue retained from an existing customer cohort after expansion and contraction.',
        patterns: ['net revenue retention', 'net dollar retention', 'nrr'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['commercial-customers-pricing'],
        priority: 9,
        valueKinds: ['percentage'],
    },
    {
        id: 'operations.churn',
        preferredLabel: 'Churn',
        description: 'Customers or recurring revenue lost during a period.',
        patterns: ['churn', 'customer attrition', 'revenue attrition'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['commercial-customers-pricing'],
        priority: 9,
        valueKinds: ['percentage'],
    },
    {
        id: 'operations.customer-acquisition-cost',
        preferredLabel: 'Customer Acquisition Cost',
        description: 'Sales and marketing cost required to acquire a customer.',
        patterns: ['customer acquisition cost', 'cac'],
        primaryDomainId: 'operating-metrics-unit-economics',
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'operations.lifetime-value',
        preferredLabel: 'Lifetime Value',
        description: 'Expected contribution or revenue generated by a customer relationship.',
        patterns: ['customer lifetime value', 'lifetime value', 'ltv'],
        primaryDomainId: 'operating-metrics-unit-economics',
        valueKinds: ['currency', 'ratio'],
    },
    {
        id: 'operations.arpu',
        preferredLabel: 'Average Revenue Per User',
        description: 'Revenue generated per active user or account.',
        patterns: ['average revenue per user', 'average revenue per account', 'arpu'],
        primaryDomainId: 'operating-metrics-unit-economics',
        valueKinds: ['currency'],
    },
    {
        id: 'operations.utilization',
        preferredLabel: 'Utilization',
        description: 'Share of available capacity actively deployed.',
        patterns: ['utilization rate', 'capacity utilization'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['percentage'],
    },
    {
        id: 'operations.unit-volume',
        preferredLabel: 'Unit Volume',
        description: 'Quantity of products or services sold or delivered.',
        patterns: ['unit volume', 'units sold', 'shipment volume'],
        primaryDomainId: 'operating-metrics-unit-economics',
        valueKinds: ['integer', 'series'],
    },
    {
        id: 'operations.yield',
        preferredLabel: 'Yield',
        description: 'Useful output as a share of total input or productive capacity.',
        patterns: ['production yield', 'manufacturing yield'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['percentage'],
    },
    {
        id: 'operations.backlog',
        preferredLabel: 'Backlog',
        description: 'Contracted or ordered business not yet recognized or delivered.',
        patterns: ['backlog', 'order backlog'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['commercial-customers-pricing'],
        priority: 8,
        valueKinds: ['currency', 'integer'],
    },
    {
        id: 'operations.throughput',
        preferredLabel: 'Throughput',
        description: 'Output processed during a specified interval.',
        patterns: ['throughput', 'production rate'],
        primaryDomainId: 'operating-metrics-unit-economics',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['integer', 'ratio'],
    },
    {
        id: 'operations.same-store-sales',
        preferredLabel: 'Same-Store Sales',
        description: 'Sales growth from comparable operating locations.',
        patterns: ['same-store sales', 'comparable store sales', 'same store sales'],
        primaryDomainId: 'operating-metrics-unit-economics',
        valueKinds: ['currency', 'percentage'],
    },

    // Commercial, Customers & Pricing
    {
        id: 'commercial.customer-concentration',
        preferredLabel: 'Customer Concentration',
        description: 'Reliance on a limited number of customers or counterparties.',
        patterns: ['customer concentration', 'concentration of customers', 'largest customer'],
        primaryDomainId: 'commercial-customers-pricing',
        secondaryDomainIds: ['risk-controls-resilience'],
        priority: 10,
        valueKinds: ['percentage', 'narrative'],
    },
    {
        id: 'commercial.pricing',
        preferredLabel: 'Pricing',
        description: 'Price levels, models, changes, and realization.',
        patterns: ['pricing', 'price increase', 'price realization'],
        primaryDomainId: 'commercial-customers-pricing',
        priority: 8,
        valueKinds: ['currency', 'percentage', 'narrative'],
    },
    {
        id: 'commercial.bookings',
        preferredLabel: 'Bookings',
        description: 'Value of customer orders or commitments received.',
        patterns: ['bookings', 'new bookings'],
        primaryDomainId: 'commercial-customers-pricing',
        valueKinds: ['currency'],
    },
    {
        id: 'commercial.sales-pipeline',
        preferredLabel: 'Sales Pipeline',
        description: 'Qualified potential business progressing through the sales process.',
        patterns: ['sales pipeline', 'deal pipeline'],
        primaryDomainId: 'commercial-customers-pricing',
        valueKinds: ['currency', 'integer'],
    },
    {
        id: 'commercial.distribution-channels',
        preferredLabel: 'Distribution Channels',
        description: 'Routes through which products or services reach customers.',
        patterns: ['distribution channel', 'distribution channels', 'channel partners'],
        primaryDomainId: 'commercial-customers-pricing',
    },
    {
        id: 'commercial.contract-duration',
        preferredLabel: 'Contract Duration',
        description: 'Committed term of a customer agreement.',
        patterns: ['contract duration', 'contract term', 'average contract length'],
        primaryDomainId: 'commercial-customers-pricing',
        secondaryDomainIds: ['transactions-contracts-commitments'],
        valueKinds: ['duration'],
    },
    {
        id: 'commercial.renewals',
        preferredLabel: 'Renewals',
        description: 'Continuation of expiring customer agreements.',
        patterns: ['renewal rate', 'contract renewals', 'customer renewals'],
        primaryDomainId: 'commercial-customers-pricing',
        valueKinds: ['percentage', 'integer'],
    },
    {
        id: 'commercial.win-rate',
        preferredLabel: 'Win Rate',
        description: 'Share of pursued opportunities converted to customers.',
        patterns: ['win rate', 'sales conversion rate'],
        primaryDomainId: 'commercial-customers-pricing',
        valueKinds: ['percentage'],
    },
    {
        id: 'commercial.discounting',
        preferredLabel: 'Discounting',
        description: 'Price reductions or concessions offered in commercial arrangements.',
        patterns: ['discounting', 'price discount', 'sales discount'],
        primaryDomainId: 'commercial-customers-pricing',
        valueKinds: ['percentage', 'currency'],
    },
    {
        id: 'commercial.demand',
        preferredLabel: 'Demand',
        description: 'Customer willingness and ability to purchase a product or service.',
        patterns: ['customer demand', 'market demand', 'demand environment'],
        primaryDomainId: 'commercial-customers-pricing',
        secondaryDomainIds: ['market-industry-competition'],
    },

    // Market, Industry & Competition
    {
        id: 'market.market-size',
        preferredLabel: 'Market Size',
        description: 'Measured value or volume of an addressed market.',
        patterns: ['market size'],
        primaryDomainId: 'market-industry-competition',
        valueKinds: ['currency', 'integer'],
    },
    {
        id: 'market.market-share',
        preferredLabel: 'Market Share',
        description: 'Company sales or volume as a share of its defined market.',
        patterns: ['market share'],
        primaryDomainId: 'market-industry-competition',
        priority: 9,
        valueKinds: ['percentage'],
    },
    {
        id: 'market.addressable-market',
        preferredLabel: 'Addressable Market',
        description: 'Potential revenue opportunity available to a product or business.',
        patterns: ['total addressable market', 'addressable market', 'tam'],
        primaryDomainId: 'market-industry-competition',
        priority: 9,
        valueKinds: ['currency', 'integer'],
    },
    {
        id: 'market.competition',
        preferredLabel: 'Competition',
        description: 'Rivalry among providers seeking the same customers or resources.',
        patterns: ['competition', 'competitive landscape', 'competitive environment'],
        primaryDomainId: 'market-industry-competition',
        priority: 8,
    },
    {
        id: 'market.barriers-to-entry',
        preferredLabel: 'Barriers to Entry',
        description: 'Structural obstacles that constrain new competitors.',
        patterns: ['barriers to entry', 'barrier to entry'],
        primaryDomainId: 'market-industry-competition',
    },
    {
        id: 'market.substitutes',
        preferredLabel: 'Substitutes',
        description: 'Alternative products or approaches satisfying the same customer need.',
        patterns: ['substitute products', 'competitive substitutes'],
        primaryDomainId: 'market-industry-competition',
    },
    {
        id: 'market.industry-growth',
        preferredLabel: 'Industry Growth',
        description: 'Change in aggregate industry revenue, volume, or capacity.',
        patterns: ['industry growth', 'market growth'],
        primaryDomainId: 'market-industry-competition',
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'market.industry-cycle',
        preferredLabel: 'Industry Cycle',
        description: 'Recurring expansion and contraction in industry activity.',
        patterns: ['industry cycle', 'cyclical downturn', 'cyclical recovery'],
        primaryDomainId: 'market-industry-competition',
    },

    // Macroeconomics, Rates & Policy
    {
        id: 'macro.gdp',
        preferredLabel: 'Gross Domestic Product',
        description: 'Market value of final goods and services produced in an economy.',
        patterns: ['gross domestic product', 'gdp'],
        primaryDomainId: 'macroeconomics-rates-policy',
        priority: 9,
        valueKinds: ['currency', 'percentage', 'series'],
    },
    {
        id: 'macro.consumer-price-index',
        preferredLabel: 'Consumer Price Index',
        description: 'Index of prices paid by consumers for a representative basket.',
        patterns: ['consumer price index', 'cpi inflation', 'cpi'],
        primaryDomainId: 'macroeconomics-rates-policy',
        priority: 9,
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'macro.pce-inflation',
        preferredLabel: 'PCE Inflation',
        description: 'Inflation measured by the personal consumption expenditures price index.',
        patterns: ['pce inflation', 'pce price index', 'personal consumption expenditures price index'],
        primaryDomainId: 'macroeconomics-rates-policy',
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'macro.unemployment-rate',
        preferredLabel: 'Unemployment Rate',
        description: 'Share of the labor force without work and actively seeking employment.',
        patterns: ['unemployment rate', 'jobless rate'],
        primaryDomainId: 'macroeconomics-rates-policy',
        priority: 8,
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'macro.policy-rate',
        preferredLabel: 'Policy Rate',
        description: 'Short-term interest rate targeted or administered by a central bank.',
        patterns: ['federal funds rate', 'fed funds rate', 'policy rate'],
        primaryDomainId: 'macroeconomics-rates-policy',
        priority: 8,
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'macro.yield-curve',
        preferredLabel: 'Yield Curve',
        description: 'Relationship between interest rates and maturities for comparable debt.',
        patterns: ['yield curve', 'term structure'],
        primaryDomainId: 'macroeconomics-rates-policy',
        secondaryDomainIds: ['capital-structure-liquidity-valuation'],
        valueKinds: ['percentage', 'series'],
    },
    {
        id: 'macro.foreign-exchange',
        preferredLabel: 'Foreign Exchange',
        description: 'Currency rates and related translation or transaction exposure.',
        patterns: ['foreign exchange', 'exchange rate', 'currency fluctuation', 'fx exposure'],
        primaryDomainId: 'macroeconomics-rates-policy',
        secondaryDomainIds: ['risk-controls-resilience'],
        valueKinds: ['ratio', 'series'],
    },
    {
        id: 'macro.commodity-prices',
        preferredLabel: 'Commodity Prices',
        description: 'Market prices for energy, metals, agricultural, and other physical inputs.',
        patterns: ['commodity prices', 'commodity price'],
        primaryDomainId: 'macroeconomics-rates-policy',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['currency', 'series'],
    },
    {
        id: 'macro.tariffs',
        preferredLabel: 'Tariffs',
        description: 'Taxes imposed on imported or exported goods.',
        patterns: ['tariff', 'tariffs', 'import duties'],
        primaryDomainId: 'macroeconomics-rates-policy',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
    {
        id: 'macro.monetary-policy',
        preferredLabel: 'Monetary Policy',
        description: 'Central-bank actions affecting money, credit, and interest rates.',
        patterns: ['monetary policy', 'quantitative easing', 'quantitative tightening'],
        primaryDomainId: 'macroeconomics-rates-policy',
    },
    {
        id: 'macro.fiscal-policy',
        preferredLabel: 'Fiscal Policy',
        description: 'Government taxation, spending, borrowing, and budget policy.',
        patterns: ['fiscal policy', 'government spending', 'fiscal stimulus'],
        primaryDomainId: 'macroeconomics-rates-policy',
    },

    // Strategy, Growth & Forward Outlook
    {
        id: 'strategy.guidance',
        preferredLabel: 'Guidance',
        description: 'Management expectations for future financial or operating performance.',
        patterns: ['guidance', 'financial outlook', 'business outlook'],
        primaryDomainId: 'strategy-growth-outlook',
        priority: 9,
        valueKinds: ['currency', 'percentage', 'narrative'],
    },
    {
        id: 'strategy.forecast',
        preferredLabel: 'Forecast',
        description: 'Quantified or narrative estimate of a future condition.',
        patterns: ['forecast', 'projection', 'projected results'],
        primaryDomainId: 'strategy-growth-outlook',
        priority: 8,
        valueKinds: ['currency', 'percentage', 'series', 'narrative'],
    },
    {
        id: 'strategy.strategic-priorities',
        preferredLabel: 'Strategic Priorities',
        description: 'Management-designated objectives receiving focused resources and attention.',
        patterns: ['strategic priorities', 'strategic priority', 'strategic objectives'],
        primaryDomainId: 'strategy-growth-outlook',
    },
    {
        id: 'strategy.organic-growth',
        preferredLabel: 'Organic Growth',
        description: 'Growth generated without acquisitions or currency translation effects.',
        patterns: ['organic growth', 'organically grown'],
        primaryDomainId: 'strategy-growth-outlook',
        valueKinds: ['percentage'],
    },
    {
        id: 'strategy.geographic-expansion',
        preferredLabel: 'Geographic Expansion',
        description: 'Entry into or scaling within additional geographic markets.',
        patterns: ['geographic expansion', 'international expansion', 'market expansion'],
        primaryDomainId: 'strategy-growth-outlook',
    },
    {
        id: 'strategy.partnerships',
        preferredLabel: 'Strategic Partnerships',
        description: 'Collaborative commercial or technical arrangements intended to create strategic value.',
        patterns: ['strategic partnership', 'strategic partnerships', 'strategic alliance'],
        primaryDomainId: 'strategy-growth-outlook',
    },
    {
        id: 'strategy.acquisitions',
        preferredLabel: 'Acquisitions',
        description: 'Purchase or combination with another business or set of assets.',
        patterns: ['acquisition', 'acquisitions', 'business combination'],
        primaryDomainId: 'strategy-growth-outlook',
        secondaryDomainIds: ['transactions-contracts-commitments'],
        priority: 8,
    },
    {
        id: 'strategy.divestitures',
        preferredLabel: 'Divestitures',
        description: 'Sale, separation, or disposal of a business or set of assets.',
        patterns: ['divestiture', 'divestitures', 'business disposal'],
        primaryDomainId: 'strategy-growth-outlook',
        secondaryDomainIds: ['transactions-contracts-commitments'],
    },
    {
        id: 'strategy.synergies',
        preferredLabel: 'Synergies',
        description: 'Expected incremental benefits from combining organizations or activities.',
        patterns: ['synergy', 'synergies', 'cost synergies', 'revenue synergies'],
        primaryDomainId: 'strategy-growth-outlook',
        secondaryDomainIds: ['transactions-contracts-commitments'],
        priority: 8,
        valueKinds: ['currency', 'percentage', 'narrative'],
    },
    {
        id: 'strategy.catalysts',
        preferredLabel: 'Catalysts',
        description: 'Events or developments expected to materially change value or perception.',
        patterns: ['catalyst', 'catalysts'],
        primaryDomainId: 'strategy-growth-outlook',
    },
    {
        id: 'strategy.milestones',
        preferredLabel: 'Milestones',
        description: 'Defined achievements used to assess progress against a plan.',
        patterns: ['milestone', 'milestones'],
        primaryDomainId: 'strategy-growth-outlook',
    },

    // Products, Technology & Intellectual Property
    {
        id: 'product.portfolio',
        preferredLabel: 'Product Portfolio',
        description: 'Set of products and services offered or under development.',
        patterns: ['product portfolio', 'product suite'],
        primaryDomainId: 'products-technology-intellectual-property',
    },
    {
        id: 'product.roadmap',
        preferredLabel: 'Product Roadmap',
        description: 'Planned sequence of product capabilities and releases.',
        patterns: ['product roadmap', 'technology roadmap'],
        primaryDomainId: 'products-technology-intellectual-property',
    },
    {
        id: 'product.research-development',
        preferredLabel: 'Research & Development',
        description: 'Investment and activity directed toward creating or improving technology and products.',
        patterns: ['research and development', 'r&d expense', 'r&d investment'],
        primaryDomainId: 'products-technology-intellectual-property',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'product.patents',
        preferredLabel: 'Patents',
        description: 'Legal rights protecting inventions and technical methods.',
        patterns: ['patent', 'patents', 'patent portfolio'],
        primaryDomainId: 'products-technology-intellectual-property',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
    {
        id: 'product.intellectual-property-licenses',
        preferredLabel: 'IP Licenses',
        description: 'Rights granted to use protected technology, content, or know-how.',
        patterns: ['intellectual property license', 'technology license', 'patent license'],
        primaryDomainId: 'products-technology-intellectual-property',
        secondaryDomainIds: ['transactions-contracts-commitments'],
    },
    {
        id: 'product.artificial-intelligence',
        preferredLabel: 'Artificial Intelligence',
        description: 'Machine-learning and artificial-intelligence capabilities, products, and dependencies.',
        patterns: ['artificial intelligence', 'generative ai', 'machine learning'],
        primaryDomainId: 'products-technology-intellectual-property',
    },
    {
        id: 'product.cloud',
        preferredLabel: 'Cloud Computing',
        description: 'Cloud-hosted infrastructure, platforms, applications, and related migration.',
        patterns: ['cloud computing', 'cloud infrastructure', 'cloud platform'],
        primaryDomainId: 'products-technology-intellectual-property',
    },
    {
        id: 'product.cybersecurity',
        preferredLabel: 'Cybersecurity Products',
        description: 'Products and services designed to protect systems, networks, and data.',
        patterns: ['cybersecurity product', 'cybersecurity platform', 'security software'],
        primaryDomainId: 'products-technology-intellectual-property',
    },
    {
        id: 'product.technical-debt',
        preferredLabel: 'Technical Debt',
        description: 'Future engineering cost created by prior technical design or implementation choices.',
        patterns: ['technical debt', 'legacy architecture'],
        primaryDomainId: 'products-technology-intellectual-property',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'product.obsolescence',
        preferredLabel: 'Technology Obsolescence',
        description: 'Risk that technology or products become outdated or noncompetitive.',
        patterns: ['technology obsolescence', 'technological obsolescence', 'product obsolescence'],
        primaryDomainId: 'products-technology-intellectual-property',
        secondaryDomainIds: ['risk-controls-resilience'],
    },

    // Operations, Assets & Supply Chain
    {
        id: 'supply-chain.suppliers',
        preferredLabel: 'Suppliers',
        description: 'Third parties providing critical goods, services, or components.',
        patterns: ['key suppliers', 'critical supplier', 'supplier base'],
        primaryDomainId: 'operations-assets-supply-chain',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'supply-chain.capacity',
        preferredLabel: 'Capacity',
        description: 'Maximum sustainable production or service-delivery capability.',
        patterns: ['production capacity', 'manufacturing capacity', 'capacity expansion'],
        primaryDomainId: 'operations-assets-supply-chain',
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'supply-chain.facilities',
        preferredLabel: 'Facilities',
        description: 'Physical locations used for production, distribution, research, or administration.',
        patterns: ['manufacturing facilities', 'production facilities', 'distribution facilities'],
        primaryDomainId: 'operations-assets-supply-chain',
    },
    {
        id: 'supply-chain.logistics',
        preferredLabel: 'Logistics',
        description: 'Movement and storage of goods across the value chain.',
        patterns: ['logistics', 'freight costs', 'transportation network'],
        primaryDomainId: 'operations-assets-supply-chain',
    },
    {
        id: 'supply-chain.lead-times',
        preferredLabel: 'Lead Times',
        description: 'Elapsed time between initiation and delivery of a process, input, or order.',
        patterns: ['lead time', 'lead times'],
        primaryDomainId: 'operations-assets-supply-chain',
        valueKinds: ['duration'],
    },
    {
        id: 'supply-chain.sourcing',
        preferredLabel: 'Sourcing',
        description: 'Strategy and process for obtaining operational inputs.',
        patterns: ['strategic sourcing', 'dual sourcing', 'single sourcing'],
        primaryDomainId: 'operations-assets-supply-chain',
    },
    {
        id: 'supply-chain.quality',
        preferredLabel: 'Quality',
        description: 'Conformance of products or services to required specifications and expectations.',
        patterns: ['quality control', 'quality assurance', 'product quality'],
        primaryDomainId: 'operations-assets-supply-chain',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'supply-chain.continuity',
        preferredLabel: 'Supply Continuity',
        description: 'Ability to maintain access to critical inputs during disruption.',
        patterns: ['supply continuity', 'supply disruption', 'supply chain disruption'],
        primaryDomainId: 'operations-assets-supply-chain',
        secondaryDomainIds: ['risk-controls-resilience'],
        priority: 8,
    },
    {
        id: 'supply-chain.geographic-footprint',
        preferredLabel: 'Geographic Footprint',
        description: 'Distribution of operations, assets, customers, and suppliers across locations.',
        patterns: ['geographic footprint', 'global footprint', 'operating footprint'],
        primaryDomainId: 'operations-assets-supply-chain',
        secondaryDomainIds: ['risk-controls-resilience'],
    },

    // Governance, Ownership & Leadership
    {
        id: 'governance.board',
        preferredLabel: 'Board of Directors',
        description: 'Governing body responsible for oversight of the organization.',
        patterns: ['board of directors', 'supervisory board'],
        primaryDomainId: 'governance-ownership-leadership',
        priority: 8,
    },
    {
        id: 'governance.committees',
        preferredLabel: 'Board Committees',
        description: 'Board-delegated groups responsible for defined oversight duties.',
        patterns: ['audit committee', 'compensation committee', 'nominating committee'],
        primaryDomainId: 'governance-ownership-leadership',
    },
    {
        id: 'governance.executives',
        preferredLabel: 'Executive Leadership',
        description: 'Senior officers responsible for organizational management and execution.',
        patterns: ['executive leadership', 'senior management', 'executive officers'],
        primaryDomainId: 'governance-ownership-leadership',
    },
    {
        id: 'governance.beneficial-ownership',
        preferredLabel: 'Beneficial Ownership',
        description: 'Direct or indirect economic or voting ownership of securities.',
        patterns: ['beneficial ownership', 'beneficial owner'],
        primaryDomainId: 'governance-ownership-leadership',
        valueKinds: ['percentage', 'integer'],
    },
    {
        id: 'governance.executive-compensation',
        preferredLabel: 'Executive Compensation',
        description: 'Salary, incentives, equity, and other remuneration of senior leadership.',
        patterns: ['executive compensation', 'named executive officer compensation'],
        primaryDomainId: 'governance-ownership-leadership',
        secondaryDomainIds: ['human-capital-organization'],
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'governance.succession',
        preferredLabel: 'Succession Planning',
        description: 'Preparation for orderly transition of critical leadership roles.',
        patterns: ['succession plan', 'succession planning', 'leadership succession'],
        primaryDomainId: 'governance-ownership-leadership',
        secondaryDomainIds: ['human-capital-organization', 'risk-controls-resilience'],
    },
    {
        id: 'governance.voting-rights',
        preferredLabel: 'Voting Rights',
        description: 'Rights attached to securities to vote on organizational matters.',
        patterns: ['voting rights', 'voting power', 'dual-class shares'],
        primaryDomainId: 'governance-ownership-leadership',
        valueKinds: ['percentage', 'integer'],
    },
    {
        id: 'governance.insider-transactions',
        preferredLabel: 'Insider Transactions',
        description: 'Transactions in securities by officers, directors, or other insiders.',
        patterns: ['insider transaction', 'insider transactions', 'insider trading activity'],
        primaryDomainId: 'governance-ownership-leadership',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },

    // Legal, Regulatory & Compliance
    {
        id: 'legal.litigation',
        preferredLabel: 'Litigation',
        description: 'Pending, threatened, or resolved judicial proceedings.',
        patterns: ['litigation', 'legal proceeding', 'legal proceedings'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['risk-controls-resilience'],
        priority: 9,
    },
    {
        id: 'legal.investigations',
        preferredLabel: 'Investigations',
        description: 'Formal or informal inquiries by regulators, law enforcement, or other authorities.',
        patterns: ['regulatory investigation', 'government investigation', 'formal investigation'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'legal.permits',
        preferredLabel: 'Permits',
        description: 'Governmental permissions required to conduct defined activities.',
        patterns: ['operating permit', 'environmental permit', 'government permit'],
        primaryDomainId: 'legal-regulatory-compliance',
    },
    {
        id: 'legal.regulatory-licenses',
        preferredLabel: 'Regulatory Licenses',
        description: 'Licenses or registrations required by law to conduct business.',
        patterns: ['regulatory license', 'operating license', 'regulatory registration'],
        primaryDomainId: 'legal-regulatory-compliance',
    },
    {
        id: 'legal.privacy',
        preferredLabel: 'Data Privacy',
        description: 'Requirements and risks concerning collection and use of personal information.',
        patterns: ['data privacy', 'privacy regulation', 'personal data protection'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'legal.antitrust',
        preferredLabel: 'Antitrust',
        description: 'Competition-law restrictions on market conduct and transactions.',
        patterns: ['antitrust', 'competition law', 'merger control'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['transactions-contracts-commitments'],
    },
    {
        id: 'legal.tax-compliance',
        preferredLabel: 'Tax Compliance',
        description: 'Compliance with tax filing, payment, reporting, and transfer-pricing obligations.',
        patterns: ['tax compliance', 'tax audit', 'transfer pricing'],
        primaryDomainId: 'legal-regulatory-compliance',
    },
    {
        id: 'legal.sanctions',
        preferredLabel: 'Sanctions',
        description: 'Restrictions imposed on transactions with designated parties or jurisdictions.',
        patterns: ['economic sanctions', 'trade sanctions', 'sanctions compliance'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'legal.regulatory-approval',
        preferredLabel: 'Regulatory Approval',
        description: 'Authorization from a regulator required before an action can proceed.',
        patterns: ['regulatory approval', 'regulatory clearance'],
        primaryDomainId: 'legal-regulatory-compliance',
        secondaryDomainIds: ['transactions-contracts-commitments'],
    },

    // Risk, Controls & Resilience
    {
        id: 'risk.risk-factors',
        preferredLabel: 'Risk Factors',
        description: 'Conditions that may materially impair objectives, performance, or value.',
        patterns: ['risk factors', 'principal risks', 'key risks'],
        primaryDomainId: 'risk-controls-resilience',
        priority: 7,
    },
    {
        id: 'risk.cybersecurity-incident',
        preferredLabel: 'Cybersecurity Incident',
        description: 'Unauthorized or harmful event affecting systems, networks, or information.',
        patterns: ['cybersecurity incident', 'cyber incident', 'data breach', 'security breach'],
        primaryDomainId: 'risk-controls-resilience',
        priority: 9,
    },
    {
        id: 'risk.internal-controls',
        preferredLabel: 'Internal Controls',
        description: 'Processes intended to provide assurance over reporting, operations, and compliance.',
        patterns: ['internal control', 'internal controls', 'control environment'],
        primaryDomainId: 'risk-controls-resilience',
        secondaryDomainIds: ['governance-ownership-leadership'],
    },
    {
        id: 'risk.audit-findings',
        preferredLabel: 'Audit Findings',
        description: 'Issues identified through internal or external assurance activities.',
        patterns: ['audit finding', 'audit findings', 'audit deficiency'],
        primaryDomainId: 'risk-controls-resilience',
    },
    {
        id: 'risk.material-weakness',
        preferredLabel: 'Material Weakness',
        description:
            'Deficiency or combination of deficiencies creating a reasonable possibility of material misstatement.',
        patterns: ['material weakness', 'material weaknesses'],
        primaryDomainId: 'risk-controls-resilience',
        secondaryDomainIds: ['financial-reporting-performance'],
        priority: 9,
    },
    {
        id: 'risk.insurance',
        preferredLabel: 'Insurance Coverage',
        description: 'Risk transfer protection and associated limits, exclusions, and deductibles.',
        patterns: ['insurance coverage', 'insurance policy', 'policy limits'],
        primaryDomainId: 'risk-controls-resilience',
        secondaryDomainIds: ['transactions-contracts-commitments'],
    },
    {
        id: 'risk.fraud',
        preferredLabel: 'Fraud',
        description: 'Intentional deception for unlawful gain or to deprive another of a right.',
        patterns: ['fraud', 'fraudulent activity', 'misappropriation'],
        primaryDomainId: 'risk-controls-resilience',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
    {
        id: 'risk.business-continuity',
        preferredLabel: 'Business Continuity',
        description: 'Preparation to sustain or restore critical operations following disruption.',
        patterns: ['business continuity', 'disaster recovery', 'continuity plan'],
        primaryDomainId: 'risk-controls-resilience',
        secondaryDomainIds: ['operations-assets-supply-chain'],
    },

    // Human Capital & Organization
    {
        id: 'people.headcount',
        preferredLabel: 'Headcount',
        description: 'Number and composition of employees or workers.',
        patterns: ['headcount', 'number of employees', 'employee count'],
        primaryDomainId: 'human-capital-organization',
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'people.hiring',
        preferredLabel: 'Hiring',
        description: 'Recruitment and addition of employees or contractors.',
        patterns: ['hiring', 'recruiting', 'talent acquisition'],
        primaryDomainId: 'human-capital-organization',
        valueKinds: ['integer'],
    },
    {
        id: 'people.attrition',
        preferredLabel: 'Employee Attrition',
        description: 'Loss of employees through voluntary or involuntary separation.',
        patterns: ['employee attrition', 'employee turnover', 'staff turnover'],
        primaryDomainId: 'human-capital-organization',
        secondaryDomainIds: ['risk-controls-resilience'],
        valueKinds: ['percentage', 'integer'],
    },
    {
        id: 'people.compensation',
        preferredLabel: 'Employee Compensation',
        description: 'Salary, wages, incentives, equity, and benefits paid to the workforce.',
        patterns: ['employee compensation', 'workforce compensation', 'compensation and benefits'],
        primaryDomainId: 'human-capital-organization',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'people.labor-relations',
        preferredLabel: 'Labor Relations',
        description: 'Relationships with employees, unions, and collective-bargaining organizations.',
        patterns: ['labor relations', 'collective bargaining', 'unionized employees'],
        primaryDomainId: 'human-capital-organization',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
    {
        id: 'people.productivity',
        preferredLabel: 'Workforce Productivity',
        description: 'Output or value created relative to workforce inputs.',
        patterns: ['workforce productivity', 'employee productivity'],
        primaryDomainId: 'human-capital-organization',
        secondaryDomainIds: ['operating-metrics-unit-economics'],
        valueKinds: ['ratio', 'percentage'],
    },
    {
        id: 'people.culture',
        preferredLabel: 'Organizational Culture',
        description: 'Shared behaviors, incentives, norms, and values within an organization.',
        patterns: ['organizational culture', 'company culture', 'workplace culture'],
        primaryDomainId: 'human-capital-organization',
    },
    {
        id: 'people.key-person',
        preferredLabel: 'Key-Person Dependency',
        description: 'Reliance on a limited number of individuals for critical knowledge or relationships.',
        patterns: ['key person risk', 'key-person dependency', 'key employee dependency'],
        primaryDomainId: 'human-capital-organization',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'people.organizational-change',
        preferredLabel: 'Organizational Change',
        description: 'Material changes to reporting lines, structure, roles, or operating model.',
        patterns: ['organizational change', 'organizational restructuring', 'reorganization'],
        primaryDomainId: 'human-capital-organization',
        secondaryDomainIds: ['strategy-growth-outlook'],
    },

    // Sustainability, Climate & Natural Resources
    {
        id: 'sustainability.scope-1-emissions',
        preferredLabel: 'Scope 1 Emissions',
        description: 'Direct greenhouse-gas emissions from owned or controlled sources.',
        patterns: ['scope 1 emissions', 'scope one emissions'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        priority: 8,
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.scope-2-emissions',
        preferredLabel: 'Scope 2 Emissions',
        description: 'Indirect greenhouse-gas emissions from purchased energy.',
        patterns: ['scope 2 emissions', 'scope two emissions'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        priority: 8,
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.scope-3-emissions',
        preferredLabel: 'Scope 3 Emissions',
        description: 'Other indirect greenhouse-gas emissions across the value chain.',
        patterns: ['scope 3 emissions', 'scope three emissions'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        priority: 8,
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.energy',
        preferredLabel: 'Energy Use',
        description: 'Energy consumed, generated, purchased, or sourced from renewable resources.',
        patterns: ['energy consumption', 'energy use', 'renewable energy'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.water',
        preferredLabel: 'Water',
        description: 'Water withdrawal, consumption, discharge, stress, and stewardship.',
        patterns: ['water consumption', 'water withdrawal', 'water usage'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['operations-assets-supply-chain'],
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.waste',
        preferredLabel: 'Waste',
        description: 'Waste generation, disposal, diversion, recycling, and hazardous materials.',
        patterns: ['waste generation', 'hazardous waste', 'waste diversion'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.biodiversity',
        preferredLabel: 'Biodiversity',
        description: 'Impacts and dependencies involving ecosystems, habitats, and species.',
        patterns: ['biodiversity', 'ecosystem impact', 'habitat loss'],
        primaryDomainId: 'sustainability-climate-natural-resources',
    },
    {
        id: 'sustainability.physical-climate-risk',
        preferredLabel: 'Physical Climate Risk',
        description: 'Acute or chronic physical effects of climate change on the organization.',
        patterns: ['physical climate risk', 'physical climate risks', 'extreme weather risk'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'sustainability.transition-risk',
        preferredLabel: 'Climate Transition Risk',
        description:
            'Policy, legal, technology, market, and reputation risk from transition to a lower-carbon economy.',
        patterns: ['climate transition risk', 'transition risks', 'transition risk'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['risk-controls-resilience'],
    },
    {
        id: 'sustainability.safety',
        preferredLabel: 'Workplace Safety',
        description: 'Worker health, safety incidents, injury rates, and prevention controls.',
        patterns: ['workplace safety', 'occupational safety', 'recordable injury rate'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['human-capital-organization'],
        valueKinds: ['integer', 'percentage'],
    },
    {
        id: 'sustainability.targets',
        preferredLabel: 'Climate Targets',
        description: 'Time-bound objectives for emissions, energy, adaptation, or other climate outcomes.',
        patterns: ['climate target', 'emissions target', 'net zero target'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['strategy-growth-outlook'],
        valueKinds: ['percentage', 'duration', 'narrative'],
    },
    {
        id: 'sustainability.environmental-liabilities',
        preferredLabel: 'Environmental Liabilities',
        description: 'Present obligations arising from remediation or environmental impacts.',
        patterns: ['environmental liability', 'environmental liabilities', 'remediation liability'],
        primaryDomainId: 'sustainability-climate-natural-resources',
        secondaryDomainIds: ['legal-regulatory-compliance'],
        valueKinds: ['currency'],
    },

    // Transactions, Contracts & Commitments
    {
        id: 'transaction.purchase-price',
        preferredLabel: 'Purchase Price',
        description: 'Consideration transferred or payable in a transaction.',
        patterns: ['purchase price', 'transaction consideration'],
        primaryDomainId: 'transactions-contracts-commitments',
        valueKinds: ['currency'],
    },
    {
        id: 'transaction.earnout',
        preferredLabel: 'Earnout',
        description: 'Contingent transaction consideration based on future performance or events.',
        patterns: ['earnout', 'earn-out', 'contingent consideration'],
        primaryDomainId: 'transactions-contracts-commitments',
        valueKinds: ['currency', 'percentage'],
    },
    {
        id: 'transaction.working-capital-adjustment',
        preferredLabel: 'Working Capital Adjustment',
        description: 'Purchase-price adjustment based on closing working capital.',
        patterns: ['working capital adjustment', 'working capital true-up'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['financial-reporting-performance'],
        valueKinds: ['currency'],
    },
    {
        id: 'transaction.representations-warranties',
        preferredLabel: 'Representations & Warranties',
        description: 'Contractual factual assertions and assurances made by transaction parties.',
        patterns: ['representations and warranties', 'representations & warranties', 'reps and warranties'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
    {
        id: 'transaction.termination-rights',
        preferredLabel: 'Termination Rights',
        description: 'Contractual rights and consequences for ending an agreement.',
        patterns: ['termination right', 'termination rights', 'termination fee'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['legal-regulatory-compliance'],
        valueKinds: ['currency', 'narrative'],
    },
    {
        id: 'transaction.lease-obligations',
        preferredLabel: 'Lease Obligations',
        description: 'Contractual commitments for the right to use identified assets.',
        patterns: ['lease obligation', 'lease obligations', 'lease liabilities'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['capital-structure-liquidity-valuation'],
        valueKinds: ['currency', 'duration'],
    },
    {
        id: 'transaction.debt-instruments',
        preferredLabel: 'Debt Instruments',
        description: 'Notes, loans, bonds, facilities, and other contractual borrowing arrangements.',
        patterns: ['debt instrument', 'debt instruments', 'credit facility', 'senior notes'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['capital-structure-liquidity-valuation'],
    },
    {
        id: 'transaction.material-contracts',
        preferredLabel: 'Material Contracts',
        description: 'Agreements material to the business, financial condition, or transaction.',
        patterns: ['material contract', 'material contracts', 'material agreement'],
        primaryDomainId: 'transactions-contracts-commitments',
        secondaryDomainIds: ['legal-regulatory-compliance'],
    },
];

const parsedConcepts = ResearchConceptDefinitionSchema.array().parse(concepts.map(defineConcept));
const conceptIds = new Set<string>();
const patternOwners = new Map<string, string>();

parsedConcepts.forEach((concept) => {
    if (conceptIds.has(concept.id)) {
        throw new Error(`Duplicate research concept identifier: ${concept.id}`);
    }
    if (concept.secondaryDomainIds.includes(concept.primaryDomainId)) {
        throw new Error(`Research concept ${concept.id} repeats its primary domain as a secondary domain.`);
    }

    concept.patterns.forEach((pattern) => {
        const normalizedPattern = pattern.toLocaleLowerCase().trim();
        const existingOwner = patternOwners.get(normalizedPattern);

        if (existingOwner) {
            throw new Error(
                `Research concept pattern "${normalizedPattern}" is owned by both ${existingOwner} and ${concept.id}.`,
            );
        }
        patternOwners.set(normalizedPattern, concept.id);
    });
    conceptIds.add(concept.id);
});

export const RESEARCH_CONCEPTS: readonly ResearchConceptDefinition[] = Object.freeze(
    parsedConcepts.map((concept) =>
        Object.freeze({
            ...concept,
            aliases: Object.freeze([...concept.aliases]),
            mappings: Object.freeze([...concept.mappings]),
            patterns: Object.freeze([...concept.patterns]),
            secondaryDomainIds: Object.freeze([...concept.secondaryDomainIds]),
            valueKinds: Object.freeze([...concept.valueKinds]),
        }),
    ),
);
export const RESEARCH_CONCEPT_BY_ID: ReadonlyMap<string, ResearchConceptDefinition> = new Map(
    RESEARCH_CONCEPTS.map((concept) => [concept.id, concept]),
);

export const getConceptsForDomain = (domainId: ResearchDomainId) =>
    RESEARCH_CONCEPTS.filter(
        (concept) => concept.primaryDomainId === domainId || concept.secondaryDomainIds.includes(domainId),
    );
